import React, { useState, useEffect } from 'react';
import {
  X, FileText, FileSpreadsheet, Loader2, Calculator, Package, Users, Truck, LayoutDashboard, Check,
  Trash2,
} from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import { exportCustomReportPDF, ReportOptions } from '../../utils/exportAnalytics';
import { exportCustomReportExcel } from '../../utils/exportExcel';

type ExportFormat = 'pdf' | 'excel';

/**
 * Επιλογές εξαγωγής PDF για τον Πίνακα Αναλυτικών: ο διαχειριστής διαλέγει
 * ποιες ενότητες θα περιέχει η αναφορά (Λογιστικά, Product Mix, Πελάτες,
 * Κανάλια) και το επίπεδο λεπτομέρειας του Product Mix. Οι επιλογές
 * αποθηκεύονται τοπικά ώστε να θυμούνται την επόμενη φορά. Τα δεδομένα
 * έρχονται από το /report-data (πλήρη — όχι τα top-10 του dashboard).
 */

interface Props {
  isOpen: boolean;
  onClose: () => void;
  storeId: number;
  storeName?: string;
  dateFrom: string;
  dateTo: string;
  analytics: any; // τα ήδη φορτωμένα δεδομένα του dashboard (για τη Σύνοψη)
  initialFormat?: ExportFormat; // ποιο κουμπί το άνοιξε (PDF ή Excel)
  // Org mode: αντικαθιστά το per-store /report-data με custom fetcher (π.χ.
  // aggregation πολλών καταστημάτων) και κρύβει την ενότητα Πελάτες (τα customer
  // records δεν ενοποιούνται μεταξύ καταστημάτων).
  fetchReportData?: (sections: string[], detail: 'summary' | 'full') => Promise<any>;
  hideCustomers?: boolean;
}

const STORAGE_KEY = 'xf_pdf_report_opts';

const DEFAULT_OPTS: ReportOptions = {
  accounting: true,
  products: true,
  customers: false,
  channels: true,
  waste: true,
  detail: 'summary',
};

const ExportReportModal: React.FC<Props> = ({ isOpen, onClose, storeId, storeName, dateFrom, dateTo, analytics, initialFormat = 'pdf', fetchReportData, hideCustomers = false }) => {
  const [opts, setOpts] = useState<ReportOptions>(DEFAULT_OPTS);
  const [format, setFormat] = useState<ExportFormat>(initialFormat);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setFormat(initialFormat);
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) setOpts({ ...DEFAULT_OPTS, ...JSON.parse(saved) });
      } catch { /* κρατάμε τα defaults */ }
    }
  }, [isOpen, initialFormat]);

  const toggle = (key: keyof Omit<ReportOptions, 'detail'>) => {
    setOpts(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const setDetail = (detail: 'summary' | 'full') => {
    setOpts(prev => {
      const next = { ...prev, detail };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const available = (['accounting', 'products', 'customers', 'channels', 'waste'] as const)
        .filter(k => !(hideCustomers && k === 'customers'));
      const sections = available.filter(k => opts[k]);
      let report: any = null;
      if (sections.length > 0) {
        if (fetchReportData) {
          report = await fetchReportData(sections, opts.detail);
        } else {
          const params = new URLSearchParams({
            from: dateFrom, to: dateTo,
            sections: sections.join(','),
            detail: opts.detail,
          });
          const res = await fetch(`${API_BASE_URL}/api/analytics/stores/${storeId}/report-data?${params}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
          });
          if (!res.ok) throw new Error('Αποτυχία φόρτωσης δεδομένων αναφοράς');
          report = await res.json();
        }
      }
      if (format === 'excel') {
        await exportCustomReportExcel({ analytics, report, options: opts, storeName, dateFrom, dateTo });
      } else {
        exportCustomReportPDF({ analytics, report, options: opts, storeName, dateFrom, dateTo });
      }
      onClose();
    } catch (e: any) {
      setError(e.message || 'Αποτυχία δημιουργίας αναφοράς');
    } finally {
      setGenerating(false);
    }
  };

  if (!isOpen) return null;

  const SectionRow: React.FC<{
    checked: boolean; onToggle: () => void; icon: React.ElementType;
    title: string; desc: string; accent: string; children?: React.ReactNode;
  }> = ({ checked, onToggle, icon: Icon, title, desc, accent, children }) => (
    <div className={`rounded-xl border-2 transition-colors ${checked ? 'border-blue-500/60 bg-blue-500/5' : 'border-line bg-surface-1/60'}`}>
      <button type="button" onClick={onToggle} className="w-full flex items-start gap-3 p-3.5 text-left">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-t1">{title}</p>
          <p className="text-xs text-t3 mt-0.5">{desc}</p>
        </div>
        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 ${checked ? 'bg-blue-600 border-blue-600' : 'border-line-strong'}`}>
          {checked && <Check className="w-3.5 h-3.5 text-t1" />}
        </div>
      </button>
      {children}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line w-full max-w-md shadow-2xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-line shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600/20 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-t1">Εξαγωγή Αναφοράς</h2>
              <p className="text-xs text-t3">Διάλεξε μορφή & ενότητες</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-t3 hover:text-t1 rounded-lg hover:bg-surface-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
          {/* Μορφή αρχείου */}
          <div className="grid grid-cols-2 gap-2 mb-1">
            <button
              type="button" onClick={() => setFormat('pdf')}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold transition-colors ${
                format === 'pdf' ? 'border-blue-500 bg-blue-500/10 text-t1 dark:text-white' : 'border-line bg-surface-1/60 text-t3'
              }`}
            >
              <FileText className="w-4 h-4" /> PDF
            </button>
            <button
              type="button" onClick={() => setFormat('excel')}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold transition-colors ${
                format === 'excel' ? 'border-emerald-500 bg-emerald-500/10 text-t1 dark:text-white' : 'border-line bg-surface-1/60 text-t3'
              }`}
            >
              <FileSpreadsheet className="w-4 h-4" /> Excel
            </button>
          </div>

          {/* Σύνοψη — πάντα ενεργή */}
          <div className="rounded-xl border-2 border-line bg-surface-1/60 opacity-80">
            <div className="flex items-start gap-3 p-3.5">
              <div className="w-9 h-9 rounded-lg bg-blue-600/15 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                <LayoutDashboard className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-t1">Σύνοψη</p>
                <p className="text-xs text-t3 mt-0.5">KPIs, ημερήσια εξέλιξη, ώρα αιχμής — πάντα περιλαμβάνεται</p>
              </div>
              <div className="w-5 h-5 rounded-md bg-surface-3 border-2 border-line-strong flex items-center justify-center shrink-0 mt-0.5">
                <Check className="w-3.5 h-3.5 text-t2" />
              </div>
            </div>
          </div>

          <SectionRow
            checked={opts.accounting} onToggle={() => toggle('accounting')}
            icon={Calculator} accent="bg-emerald-600/15 text-emerald-600 dark:text-emerald-400"
            title="Λογιστικά"
            desc="Εισπράξεις ανά τρόπο πληρωμής, παραστατικά, ανάλυση ΦΠΑ, τέλη — συμφωνεί με τα κλεισίματα ταμείου"
          />

          <SectionRow
            checked={opts.products} onToggle={() => toggle('products')}
            icon={Package} accent="bg-purple-600/15 text-purple-600 dark:text-purple-400"
            title="Product Mix"
            desc="Όλα τα προϊόντα και οι κατηγορίες με ποσότητες, τζίρο και μερίδια"
          >
            {opts.products && (
              <div className="px-3.5 pb-3.5 flex gap-2">
                <button
                  type="button" onClick={() => setDetail('summary')}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                    opts.detail === 'summary' ? 'bg-purple-600 border-purple-500 text-white' : 'bg-surface-2 border-line-strong text-t2'
                  }`}
                >
                  Συνοπτικό
                  <span className="block font-normal opacity-75">κατηγορίες + όλα τα προϊόντα</span>
                </button>
                <button
                  type="button" onClick={() => setDetail('full')}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                    opts.detail === 'full' ? 'bg-purple-600 border-purple-500 text-white' : 'bg-surface-2 border-line-strong text-t2'
                  }`}
                >
                  Αναλυτικό
                  <span className="block font-normal opacity-75">+ μεγέθη, options, extras</span>
                </button>
              </div>
            )}
          </SectionRow>

          {!hideCustomers && (
            <SectionRow
              checked={opts.customers} onToggle={() => toggle('customers')}
              icon={Users} accent="bg-cyan-600/15 text-cyan-600 dark:text-cyan-400"
              title="Ανάλυση Πελατών"
              desc="Σύνολο, νέοι στην περίοδο, κορυφαίοι πελάτες σε τζίρο & παραγγελίες"
            />
          )}

          <SectionRow
            checked={opts.channels} onToggle={() => toggle('channels')}
            icon={Truck} accent="bg-amber-600/15 text-amber-700 dark:text-amber-400"
            title="Κανάλια εξυπηρέτησης"
            desc="Take away / Delivery / πλατφόρμες — τζίρος, μέση παραγγελία, τρόποι πληρωμής"
          />

          <SectionRow
            checked={opts.waste} onToggle={() => toggle('waste')}
            icon={Trash2} accent="bg-orange-600/15 text-orange-600 dark:text-orange-400"
            title="Φύρα"
            desc="Ανά προϊόν, λόγο και ημέρα (αξία σε τιμές πώλησης) — μόνο όπου είναι ενεργή η καταγραφή"
          />

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-700 dark:text-red-300 text-sm">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-4 border-t border-line shrink-0">
          <button
            onClick={generate}
            disabled={generating}
            className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
          >
            {generating ? <Loader2 className="w-5 h-5 animate-spin" /> : (format === 'excel' ? <FileSpreadsheet className="w-5 h-5" /> : <FileText className="w-5 h-5" />)}
            {generating ? 'Προετοιμασία δεδομένων…' : (format === 'excel' ? 'Δημιουργία Excel' : 'Δημιουργία PDF')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportReportModal;
